import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SettingsPageLayout from './SettingsPageLayout';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  Chip,
  LinearProgress,
  CircularProgress,
  Link,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api, { enhancedApi } from '../../api';

const STATUS_LABEL = {
  active: { label: 'Active', color: 'success' },
  trialing: { label: 'Trial', color: 'info' },
  past_due: { label: 'Past due', color: 'error' },
  canceled: { label: 'Canceled', color: 'default' },
  inactive: { label: 'Inactive', color: 'default' },
  unpaid: { label: 'Unpaid', color: 'error' },
};

const BillingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [subPayload, setSubPayload] = useState(null);
  const [seats, setSeats] = useState(null);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [subRes, seatRes] = await Promise.all([
          api.get('/api/platform/subscription/status/'),
          enhancedApi.getSeatInfo().catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setSubPayload(subRes.data);
        const sd = seatRes.data?.data ?? seatRes.data ?? {};
        setSeats(sd);
      } catch {
        if (alive) setError('Failed to load billing information');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const handleSubscribe = async () => {
    setActionLoading('sub');
    try {
      const res = await api.post('/api/platform/subscription/checkout/', { plan: 'pro' });
      window.location.href = res.data.url;
    } catch {
      setActionLoading('');
    }
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const res = await api.post('/api/platform/subscription/portal/');
      window.location.href = res.data.url;
    } catch {
      setActionLoading('');
    }
  };

  const plan = subPayload?.plan_details || {};
  const subscription = subPayload?.subscription || {};
  const statusKey = subscription.status || 'inactive';
  const statusCfg = STATUS_LABEL[statusKey] || STATUS_LABEL.inactive;
  const hasActiveSub = ['active', 'trialing'].includes(statusKey);

  const used = seats?.used ?? 0;
  const limit = seats?.limit ?? 0;
  const isUnlimited = seats?.unlimited === true;
  const pct = isUnlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));

  return (
    <SettingsPageLayout
      title="Billing & Seats"
      description="Platform subscription, how many seats you are using, and payment details."
      noCard
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  Current plan
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {plan.name || 'Firmflow Pro'}
                  </Typography>
                  <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {plan.price != null ? `$${plan.price}/month` : 'Pricing based on your agreement'}
                </Typography>
              </Box>
              {!hasActiveSub ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={actionLoading === 'sub' ? <CircularProgress size={18} color="inherit" /> : <TrendingUpIcon />}
                  disabled={!!actionLoading}
                  onClick={handleSubscribe}
                >
                  Upgrade plan
                </Button>
              ) : null}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Seat usage
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {used}
                  {!isUnlimited && limit ? ` / ${limit}` : ''}
                  {isUnlimited ? ' (unlimited)' : ''}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={isUnlimited ? 0 : pct}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': { borderRadius: 1 },
                }}
              />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              Payment method
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Update the card on file and download invoices from the billing portal.
            </Typography>
            <Button
              variant="outlined"
              startIcon={actionLoading === 'portal' ? <CircularProgress size={18} /> : <CreditCardIcon />}
              disabled={!!actionLoading}
              onClick={handlePortal}
            >
              Manage payment method
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              For Stripe Connect and receiving client payments, see{' '}
              <Link component={RouterLink} to="/settings/integrations" underline="hover">
                Integrations
              </Link>
              .
            </Typography>
          </Paper>
        </>
      )}
    </SettingsPageLayout>
  );
};

export default BillingSettings;
